import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, FolderOpen, ClipboardList, ArrowRight,
  Zap, FileText, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { getPIs, getDocuments } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [piCount, setPiCount] = useState(0);
  const [docCount, setDocCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const [trackerCount, setTrackerCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pis, docs, tracker, log] = await Promise.all([
          getPIs(),
          getDocuments(),
          fetch(`${API_URL}/api/tracker`).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/api/answer-log?is_enrollment_commit=true`).then(r => r.ok ? r.json() : []),
        ]);
        setPiCount(pis.length);
        setDocCount(docs.length);
        setTrackerCount(Array.isArray(tracker) ? tracker.length : 0);
        setRecent(Array.isArray(log) ? log.slice(0, 8) : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">StrydeFeast</h1>
        <p className="text-slate-600 mt-1">
          Quick Answer everywhere on the web · Auto-tracked submissions and patient commitments.
        </p>
      </div>

      {/* How it works */}
      <Card className="stryde-card border-amber-200 bg-gradient-to-br from-amber-50 to-white">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-lg">How StrydeFeast works</h3>
              <ol className="mt-2 text-sm text-slate-700 space-y-1 list-decimal list-inside">
                <li>Install the Chrome extension and sign in.</li>
                <li>Open any feasibility form on the web (Veeva, JotForm, Google, sponsor portals).</li>
                <li>Click into a question field. The <b className="text-amber-700">⚡ Quick Answer</b> badge appears.</li>
                <li>Click it → get a Stryde answer → click <b>Insert</b>. The answer drops into the field.</li>
                <li>Every insert is auto-logged to the <Link to="/commitments" className="text-blue-600 underline">Commitments</Link> view so your team sees what we promised.</li>
              </ol>
              <div className="mt-3">
                <a
                  href="/stryde-extension.zip"
                  className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:underline"
                >
                  Download the latest extension <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard to="/settings" icon={Users} color="purple" value={piCount} label="Principal investigators" loading={loading} />
        <StatCard to="/documents" icon={FolderOpen} color="blue" value={docCount} label="Documents" loading={loading} />
        <StatCard to="/tracker" icon={ClipboardList} color="amber" value={trackerCount} label="Tracker entries" loading={loading} />
        <StatCard to="/commitments" icon={Zap} color="emerald" value={recent.length} label="Recent commitments" loading={loading} />
      </div>

      {/* Recent commitments */}
      <Card className="stryde-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-slate-900 font-['Manrope']">Recent patient commitments</h3>
            <Link to="/commitments" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14" />)}</div>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No commitments logged yet. Once your team uses the extension&apos;s ⚡ Quick Answer on a feasibility form, every Insert lands here automatically.
            </div>
          ) : (
            <div className="space-y-2">
              {recent.map(row => (
                <div key={row.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 hover:border-slate-300">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{row.question || '—'}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {row.sponsor || 'No sponsor'} · {row.pi_name || 'No PI'} · {row.indication || ''}
                    </p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border shrink-0">
                    {row.patient_count}{row.patient_count_min ? ` (${row.patient_count_min}-${row.patient_count_max})` : ''} pts
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ to, icon: Icon, color, value, label, loading }) {
  const tones = {
    purple:  'bg-purple-100 text-purple-700',
    blue:    'bg-blue-100 text-blue-700',
    amber:   'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <Link to={to} className="block">
      <Card className="stryde-card hover:shadow-md transition-shadow">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tones[color]}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              {loading ? <Skeleton className="h-7 w-16" /> : <p className="text-2xl font-bold text-slate-900">{value}</p>}
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
